import { z } from 'zod';

/**
 * T16 (spec §4.5) — a validated IANA zone name.
 *
 * `safeTimeZone(tz, fallback)` (`C/utils/date.ts`) silently FALLS BACK on an
 * unknown zone, which is right for a display series (`overview` uses it that
 * way) and wrong for a statement: a typo would quietly move every month
 * boundary and the reader would never learn that the figures are not the
 * ones they asked for. Here an unusable zone is a refusal.
 *
 * Fix round 1 (review finding, "important") — `new Intl.DateTimeFormat(...,
 * { timeZone })` is NOT an IANA-name check: on Node 24 it also accepts POSIX
 * offset strings (`+01:00`, `-0500`), lowercase `utc`, POSIX zone names
 * (`EST5EDT`) and `GMT+1`. Postgres' `AT TIME ZONE` reads an offset string
 * with the OPPOSITE (POSIX) sign, so `tz: '+01:00'` made JS and SQL bucket
 * the same instant into different months and silently dropped ledger money
 * from the statement (no anomaly, no error — the row just never appeared).
 * Accept literal `UTC` or an Area/Location name recognized by Intl. The
 * supported-values list contains preferred names, not every valid IANA alias:
 * list misses must still pass the shape check and DateTimeFormat probe.
 * Region-name casing follows Intl consistently with the missing-API fallback;
 * unqualified names, including lowercase `utc`, remain rejected.
 * `+01:00`, `-0500`, `EST5EDT` and `GMT+1` must never reach the probe.
 */
// Final review C1 — NOTHING here may run at module load. `@mfw/common` is CJS
// and the frontend's main chunk requires this barrel before React mounts,
// while `rspack.config.js` targets chrome >= 87 / edge >= 88 / firefox >= 78 /
// safari >= 14 — all older than `Intl.supportedValuesOf` (Chrome 99,
// Firefox 93, Safari 15.4). A top-level call there is a `TypeError` on a blank
// page. The set is therefore built on first use and only if the API exists;
// where it does not, a shape check plus a `DateTimeFormat` probe reproduces
// the same verdicts (a real `Area/Location` name is accepted, an offset or a
// POSIX zone name is not).
//
// `Intl.supportedValuesOf` (ECMA-402) is not in the `lib.es2021.d.ts` this
// package compiles against; the narrow local type avoids `any`.
const intlWithSupportedValuesOf = Intl as typeof Intl & {
  supportedValuesOf?: (key: 'timeZone') => string[];
};

let supportedTimeZonesCache: Set<string> | null = null;

/** The `Intl` zone set, built once — or `null` where the API is missing. */
function supportedTimeZones(): Set<string> | null {
  if (typeof intlWithSupportedValuesOf.supportedValuesOf !== 'function') {
    return null;
  }
  if (supportedTimeZonesCache === null) {
    supportedTimeZonesCache = new Set<string>(
      intlWithSupportedValuesOf.supportedValuesOf('timeZone'),
    );
  }
  return supportedTimeZonesCache;
}

/** An IANA database name is `Area/Location`, optionally with a third segment.
 *  Offsets (`+01:00`, `-0500`), POSIX names (`EST5EDT`) and `GMT+1` are not. */
const IANA_ZONE_NAME_SHAPE = /^[A-Za-z_]+\/[A-Za-z0-9_+\-/]+$/;

export function isIanaTimeZone(timeZone: string) {
  if (timeZone === 'UTC') return true;
  const supported = supportedTimeZones();
  if (supported?.has(timeZone)) return true;
  if (!IANA_ZONE_NAME_SHAPE.test(timeZone)) return false;
  try {
    return Boolean(
      new Intl.DateTimeFormat('en-US', { timeZone }).resolvedOptions().timeZone,
    );
  } catch {
    return false;
  }
}

export const IanaTimeZoneDto = z
  .string()
  .refine(isIanaTimeZone, { message: 'VALIDATION__InvalidTimezone' });
